import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ACCESS_TOKEN_STORAGE_KEY } from '@/api';
import AppErrorBoundary from '@/components/AppErrorBoundary';

import Login from '@/pages/Login';
import Register from '@/pages/Register';
import CustomerLayout from '@/layouts/CustomerLayout';
import AdminLayout from '@/layouts/AdminLayout';

import CustomerDashboard from '@/pages/customer/Dashboard';
import RFQs from '@/pages/customer/demaxtore/RFQs';
import PurchaseOrders from '@/pages/customer/demaxtore/PurchaseOrders';
import InspectionReport from '@/pages/customer/demaxtore/InspectionReport';
import DemaxtoreNotifications from '@/pages/customer/demaxtore/Notifications';
import LiveShipmentTracking from '@/pages/customer/demaxtore/LiveShipmentTracking';
import LiveBids from '@/pages/customer/commoditybid/LiveBids';
import BidHistory from '@/pages/customer/commoditybid/History';
import FreightRequests from '@/pages/customer/freightiq/FreightRequests';
import ForwarderOffers from '@/pages/customer/freightiq/ForwarderOffers';
import FreightHistory from '@/pages/customer/freightiq/FreightHistory';
import Messages from '@/pages/customer/Messages';
import Profile from '@/pages/customer/Profile';

import AdminDashboard from '@/pages/admin/Dashboard';
import Customers from '@/pages/admin/Customers';
import AdminRFQs from '@/pages/admin/RFQManagement';
import TradeActivity from '@/pages/admin/TradeActivity';
import AdminCommodity from '@/pages/admin/CommodityManagement';
import AdminFreight from '@/pages/admin/FreightManagement';
import AdminOffers from '@/pages/admin/Offers';
import AdminPurchaseOrders from '@/pages/admin/PurchaseOrders';
import AdminInspectionReports from '@/pages/admin/InspectionReports';
import CRM from '@/pages/admin/CRM';
import Content from '@/pages/admin/Content';
import Users from '@/pages/admin/Users';
import Settings from '@/pages/admin/Settings';

const Loading = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-sm text-slate-500 font-body">Loading...</p>
    </div>
  </div>
);

const RootRedirect = () => {
  const { user, authInitialized } = useAuth();
  if (!authInitialized) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === 'super_admin' ? '/admin/dashboard' : '/dashboard'} replace />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, authInitialized } = useAuth();
  if (!authInitialized) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles) {
    if (!user.role) return <Navigate to="/login" replace />;
    if (!allowedRoles.includes(user.role)) {
      return <Navigate to={user.role === 'super_admin' ? '/admin/dashboard' : '/dashboard'} replace />;
    }
  }
  return children;
};

/**
 * Login/register: show form when there is no token, or after session check.
 * Only redirect away when `user.role` is set — never `<Navigate to="/login" />` while already
 * on /login with a broken session (that omitted `children` and caused a blank screen on customer.demaxtore.com).
 */
const PublicRoute = ({ children }) => {
  const { user, authInitialized } = useAuth();
  const hasToken = Boolean(
    typeof window !== 'undefined' && window.localStorage?.getItem(ACCESS_TOKEN_STORAGE_KEY)?.trim(),
  );
  if (!authInitialized && hasToken) return <Loading />;
  if (authInitialized && user?.role) {
    return <Navigate to={user.role === 'super_admin' ? '/admin/dashboard' : '/dashboard'} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/" element={<RootRedirect />} />

      {/* Customer Routes */}
      <Route element={<ProtectedRoute allowedRoles={['customer']}><CustomerLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<CustomerDashboard />} />
        <Route path="/demaxtore/rfqs" element={<RFQs />} />
        <Route path="/demaxtore/purchase-orders" element={<PurchaseOrders />} />
        <Route path="/demaxtore/inspection-report" element={<InspectionReport />} />
        <Route path="/demaxtore/notifications" element={<DemaxtoreNotifications />} />
        <Route path="/demaxtore/live-shipment-tracking" element={<LiveShipmentTracking />} />
        <Route path="/commoditybid/requests" element={<Navigate to="/dashboard" replace />} />
        <Route path="/commoditybid/live-bids" element={<LiveBids />} />
        <Route path="/commoditybid/history" element={<BidHistory />} />
        <Route path="/freightiq/requests" element={<FreightRequests />} />
        <Route path="/freightiq/offers" element={<ForwarderOffers />} />
        <Route path="/freightiq/history" element={<FreightHistory />} />
        <Route path="/freightiq/tracking" element={<Navigate to="/demaxtore/live-shipment-tracking" replace />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/profile" element={<Profile />} />
      </Route>

      {/* Admin Routes */}
      <Route element={<ProtectedRoute allowedRoles={['super_admin']}><AdminLayout /></ProtectedRoute>}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/customers" element={<Customers />} />
        <Route path="/admin/trade-activity" element={<TradeActivity />} />
        <Route path="/admin/rfqs" element={<AdminRFQs />} />
        <Route path="/admin/commodity" element={<AdminCommodity />} />
        <Route path="/admin/freight" element={<AdminFreight />} />
        <Route path="/admin/offers" element={<AdminOffers />} />
        <Route path="/admin/purchase-orders" element={<AdminPurchaseOrders />} />
        <Route path="/admin/inspection-reports" element={<AdminInspectionReports />} />
        <Route path="/admin/crm" element={<CRM />} />
        <Route path="/admin/content" element={<Content />} />
        <Route path="/admin/users" element={<Users />} />
        <Route path="/admin/settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
